import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
    CognitoUserSession,
    CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const UserPool = new CognitoUserPool({
    UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID!,
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
});

const mapSession = (session: CognitoUserSession, username: string) => ({
    accessToken: session.getIdToken().getJwtToken(),
    user: {
        id: session.getIdToken().payload['sub'] as string,
        email: username,
        displayName: username,
    },
});

export const signIn = (email: string, password: string): Promise<any> =>
    new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: UserPool });
        const authDetails = new AuthenticationDetails({ Username: email, Password: password });
        cognitoUser.authenticateUser(authDetails, {
            onSuccess: (session) => resolve(mapSession(session, email)),
            onFailure: (err) => reject(err),
            newPasswordRequired: () =>
                reject({ challengeName: 'NEW_PASSWORD_REQUIRED', cognitoUser }),
        });
    });

export const completeNewPassword = (cognitoUser: any, newPassword: string): Promise<any> =>
    new Promise((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
            onSuccess: (session: CognitoUserSession) =>
                resolve(mapSession(session, cognitoUser.getUsername())),
            onFailure: (err: any) => reject(err),
        });
    });

export const refreshSession = (): Promise<any> =>
    new Promise((resolve) => {
        const currentUser = UserPool.getCurrentUser();
        if (!currentUser) {
            resolve(null);
            return;
        }
        currentUser.getSession((err: any, session: CognitoUserSession | null) => {
            if (err || !session || !session.isValid()) {
                resolve(null);
                return;
            }
            resolve(mapSession(session, currentUser.getUsername()));
        });
    });

export const signOut = () => {
    const currentUser = UserPool.getCurrentUser();
    if (currentUser) currentUser.signOut();
};

export const signUp = (email: string, password: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const emailAttr = new CognitoUserAttribute({ Name: 'email', Value: email });
        UserPool.signUp(email, password, [emailAttr], [], (err) => {
            if (err) reject(err);
            else resolve();
        });
    });

export const confirmSignUp = (email: string, code: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: UserPool });
        cognitoUser.confirmRegistration(code, true, (err) => {
            if (err) reject(err);
            else resolve();
        });
    });

export const forgotPassword = (email: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: UserPool });
        cognitoUser.forgotPassword({
            onSuccess: () => resolve(),
            onFailure: (err) => reject(err),
        });
    });

export const confirmForgotPassword = (email: string, code: string, newPassword: string): Promise<void> =>
    new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: UserPool });
        cognitoUser.confirmPassword(code, newPassword, {
            onSuccess: () => resolve(),
            onFailure: (err) => reject(err),
        });
    });
